import React from "react";
import { Tooltip } from "@mui/material";
import { CrossIcon, PlusIcon } from "../../../../components/Icons";
import { colors } from "../../../../theme/colors";
import {
  EditChipsContainer,
  EditChip,
  EditChipGroup,
  EditChipGroupItem,
  EditChipRemove,
  EditAddButton,
  TooltipAnchor,
} from "./TreatmentStepChips.styled";

interface TreatmentStepEditChipsProps {
  values: string[];
  onRemove: (value: string) => void;
  onAddClick: () => void;
  addButtonVariant?: "code" | "icon";
  addButtonLabel?: string;
  disabled?: boolean;
  disabledReason?: string;
  error?: boolean;
  errorReason?: string;
  /** When true, selected value chips use error styling + tooltip. */
  valuesError?: boolean;
  valuesErrorReason?: string;
  /**
   * Values that belong together and can only be removed as a whole (a Custom
   * Procedure bundle). Rendered as one pill ahead of the individual chips.
   */
  group?: {
    values: string[];
    onRemove: () => void;
    /** Tooltip on the pill, e.g. the procedure name. */
    reason?: string;
    removeLabel?: string;
  };
}

const TreatmentStepEditChips: React.FC<TreatmentStepEditChipsProps> = ({
  values,
  onRemove,
  onAddClick,
  addButtonVariant = "icon",
  addButtonLabel,
  disabled = false,
  disabledReason,
  error = false,
  errorReason,
  valuesError = false,
  valuesErrorReason,
  group,
}) => {
  const addButton = (
    <EditAddButton
      type="button"
      variant={addButtonVariant}
      error={error && !disabled}
      aria-label={addButtonLabel || "Add"}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        if (disabled) return;
        onAddClick();
      }}
    >
      {addButtonVariant === "icon" ? (
        <PlusIcon
          width="14px"
          height="14px"
          fill={
            disabled
              ? colors.text.secondary
              : error
                ? colors.status.error
                : colors.border.medium
          }
        />
      ) : (
        addButtonLabel
      )}
    </EditAddButton>
  );

  const wrappedAddButton = (() => {
    if (disabled && disabledReason) {
      return (
        <Tooltip title={disabledReason} arrow placement="top">
          <TooltipAnchor>{addButton}</TooltipAnchor>
        </Tooltip>
      );
    }
    if (error && errorReason && !disabled) {
      return (
        <Tooltip title={errorReason} arrow placement="bottom">
          <TooltipAnchor>{addButton}</TooltipAnchor>
        </Tooltip>
      );
    }
    return addButton;
  })();

  const groupPill = (() => {
    if (!group?.values.length) return null;

    const pill = (
      <EditChipGroup>
        {group.values.map((value) => (
          <EditChipGroupItem key={value}>{value}</EditChipGroupItem>
        ))}
        <EditChipRemove
          type="button"
          aria-label={group.removeLabel || "Remove procedure"}
          onClick={(event) => {
            event.stopPropagation();
            group.onRemove();
          }}
        >
          <CrossIcon width="10px" height="10px" fill={colors.text.action} />
        </EditChipRemove>
      </EditChipGroup>
    );

    if (!group.reason) return pill;
    return (
      <Tooltip title={group.reason} arrow placement="top">
        <TooltipAnchor>{pill}</TooltipAnchor>
      </Tooltip>
    );
  })();

  return (
    <EditChipsContainer>
      {groupPill}
      {values.map((value) => {
        const chip = (
          <EditChip error={valuesError}>
            <span>{value}</span>
            <EditChipRemove
              type="button"
              error={valuesError}
              aria-label={`Remove ${value}`}
              onClick={(event) => {
                event.stopPropagation();
                onRemove(value);
              }}
            >
              <CrossIcon
                width="10px"
                height="10px"
                fill={valuesError ? colors.text.white : colors.text.action}
              />
            </EditChipRemove>
          </EditChip>
        );

        if (valuesError && valuesErrorReason) {
          return (
            <Tooltip
              key={value}
              title={valuesErrorReason}
              arrow
              placement="top"
            >
              <TooltipAnchor>{chip}</TooltipAnchor>
            </Tooltip>
          );
        }

        return <React.Fragment key={value}>{chip}</React.Fragment>;
      })}
      {wrappedAddButton}
    </EditChipsContainer>
  );
};

export default TreatmentStepEditChips;
